// UltraFlex Staff Self-Service Portal — Enhanced with Backup Scanner
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Calendar, Clock, Umbrella, User, QrCode, TrendingUp,
  Users, Building2, Hash, Mail, Phone, Edit3, Save, X,
  ChevronLeft, ChevronRight, Check, Target, Activity, Award,
  Megaphone, UserCheck, ClipboardList, ScanLine, LogIn, LogOut,
  Camera, CameraOff, ShieldCheck, ShieldX, AlertTriangle, RotateCcw
} from 'lucide-react';
import WalletButtons from '../../components/WalletButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const SLOTS = ['morning','afternoon','evening'];

// ============ STAFF BACKUP SCANNER COMPONENT ============
const StaffBackupScanner = ({ token, user }) => {
  const [direction, setDirection] = useState('entry');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const scannerRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers })
      .then(r => r.json())
      .then(d => {
        const items = d.items || d || [];
        setSites(items);
        if (items.length > 0) {
          const userSite = user?.site_ids?.[0];
          setSiteId(userSite || items[0].id);
        }
      })
      .catch(() => {});
    return () => stopScanning();
  }, []);

  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current.clear().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScanning = useCallback(async () => {
    if (!siteId) { setScanError('Please select a site first.'); return; }
    setScanResult(null);
    setScanError(null);

    try {
      if (scannerRef.current) {
        await scannerRef.current.stop().catch(() => {});
        await scannerRef.current.clear().catch(() => {});
      }

      const qr = new Html5Qrcode("staff-qr-scanner");
      scannerRef.current = qr;
      setScanning(true);

      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
        (decodedText) => {
          processScan(decodedText);
          qr.stop().catch(() => {});
          setScanning(false);
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      setScanError(`Camera error: ${err.message || err}. Make sure camera permissions are granted.`);
    }
  }, [siteId, direction]);

  const processScan = async (tokenId) => {
    setProcessing(true);
    setScanResult(null);
    setScanError(null);

    try {
      const res = await fetch(`${API_URL}/api/access/staff-scan`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ token_id: tokenId, site_id: siteId, direction })
      });
      const data = await res.json();

      if (!res.ok) {
        setScanError(data.detail || 'Scan failed');
        setRecentScans(prev => [{ time: new Date(), direction, allowed: false, name: 'Unknown', reason: data.detail }, ...prev].slice(0, 20));
      } else {
        setScanResult(data);
        setRecentScans(prev => [{
          time: new Date(),
          direction: data.direction || direction,
          allowed: data.allowed,
          name: data.member_name || 'Unknown',
          reason: data.reason || null,
          accountType: data.account_type,
          subscriptionStatus: data.subscription_status,
        }, ...prev].slice(0, 20));
      }
    } catch (err) {
      setScanError('Network error — check your connection.');
    } finally {
      setProcessing(false);
    }
  };

  // Manual token input for testing/fallback
  const [manualToken, setManualToken] = useState('');
  const handleManualScan = () => {
    if (manualToken.trim()) {
      processScan(manualToken.trim());
      setManualToken('');
    }
  };

  const siteName = sites.find(s => s.id === siteId)?.name || '';

  return (
    <div className="space-y-4" data-testid="staff-backup-scanner">
      {/* Scanner Controls */}
      <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
        <div className="flex items-center gap-3 mb-4">
          <ScanLine className="w-5 h-5 text-uf-red" />
          <h3 className="text-sm font-semibold text-uf-text">Backup Access Scanner</h3>
        </div>
        <p className="text-uf-text-muted text-xs mb-4">
          Use your device camera to scan a member's QR code when the primary scanner is unavailable.
        </p>

        {/* Site Selector */}
        <div className="mb-4">
          <label className="text-uf-text-muted text-xs block mb-1">Active Site</label>
          <select
            value={siteId}
            onChange={e => setSiteId(e.target.value)}
            className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
            data-testid="scanner-site-select"
          >
            <option value="">Select site...</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Direction Toggle */}
        <div className="mb-4">
          <label className="text-uf-text-muted text-xs block mb-2">Scan Direction</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setDirection('entry')}
              data-testid="direction-entry-btn"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all ${
                direction === 'entry'
                  ? 'bg-emerald-500/20 text-emerald-400 ring-2 ring-emerald-500/50'
                  : 'bg-uf-surface-hi text-uf-text-muted hover:text-uf-text'
              }`}
            >
              <LogIn className="w-4 h-4" /> Entry
            </button>
            <button
              onClick={() => setDirection('exit')}
              data-testid="direction-exit-btn"
              className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all ${
                direction === 'exit'
                  ? 'bg-orange-500/20 text-orange-400 ring-2 ring-orange-500/50'
                  : 'bg-uf-surface-hi text-uf-text-muted hover:text-uf-text'
              }`}
            >
              <LogOut className="w-4 h-4" /> Exit
            </button>
          </div>
        </div>

        {/* Camera Scanner */}
        <div className="mb-4">
          <div
            id="staff-qr-scanner"
            ref={scannerContainerRef}
            className={`w-full max-w-sm mx-auto rounded-lg overflow-hidden bg-black ${scanning ? 'h-[300px]' : 'h-0'}`}
            data-testid="qr-scanner-viewport"
          />
          <div className="flex gap-2 mt-3">
            {!scanning ? (
              <button
                onClick={startScanning}
                disabled={processing || !siteId}
                data-testid="start-scan-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-uf-red text-white py-3 rounded-lg text-sm font-semibold disabled:opacity-50 hover:bg-uf-red/90 transition-colors"
              >
                <Camera className="w-4 h-4" />
                {direction === 'entry' ? 'Scan Entry' : 'Scan Exit'}
              </button>
            ) : (
              <button
                onClick={stopScanning}
                data-testid="stop-scan-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-uf-surface-hi text-uf-text py-3 rounded-lg text-sm font-semibold border border-uf-border"
              >
                <CameraOff className="w-4 h-4" /> Stop Scanner
              </button>
            )}
          </div>
        </div>

        {/* Manual Token Fallback */}
        <details className="mt-3">
          <summary className="text-uf-text-muted text-xs cursor-pointer hover:text-uf-text">
            Manual entry (member number / QR token)
          </summary>
          <div className="flex gap-2 mt-2">
            <input
              value={manualToken}
              onChange={e => setManualToken(e.target.value)}
              placeholder="e.g. UF-000001"
              className="flex-1 bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm"
              data-testid="manual-token-input"
              onKeyDown={e => e.key === 'Enter' && handleManualScan()}
            />
            <button
              onClick={handleManualScan}
              disabled={!manualToken.trim() || processing || !siteId}
              data-testid="manual-scan-btn"
              className="bg-uf-red text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              Submit
            </button>
          </div>
        </details>
      </div>

      {/* Processing indicator */}
      {processing && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto mb-2" />
          <p className="text-uf-text-muted text-sm">Processing scan...</p>
        </div>
      )}

      {/* Scan Result */}
      {scanResult && !processing && (
        <div
          data-testid="scan-result"
          className={`rounded-xl border p-5 ${
            scanResult.allowed
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            {scanResult.allowed ? (
              <ShieldCheck className="w-8 h-8 text-emerald-400" />
            ) : (
              <ShieldX className="w-8 h-8 text-red-400" />
            )}
            <div>
              <p className={`text-lg font-bold ${scanResult.allowed ? 'text-emerald-400' : 'text-red-400'}`}>
                {scanResult.allowed ? 'ACCESS GRANTED' : 'ACCESS DENIED'}
              </p>
              <p className="text-uf-text-muted text-xs">
                {scanResult.direction === 'entry' ? 'Entry' : 'Exit'} &middot; {siteName}
              </p>
            </div>
          </div>
          {scanResult.member_name && (
            <p className="text-uf-text text-sm font-medium">{scanResult.member_name}</p>
          )}
          {scanResult.account_type && (
            <p className="text-uf-text-muted text-xs capitalize">Account: {scanResult.account_type}</p>
          )}
          {scanResult.subscription_status && (
            <p className="text-uf-text-muted text-xs capitalize">Subscription: {scanResult.subscription_status}</p>
          )}
          {scanResult.instructions && (
            <p className="text-uf-text-muted text-xs mt-2 italic">{scanResult.instructions}</p>
          )}
          {scanResult.is_grace && (
            <div className="mt-2 flex items-center gap-2 text-amber-400 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" /> Grace period — access granted temporarily
            </div>
          )}
          <button
            onClick={() => { setScanResult(null); }}
            data-testid="scan-again-btn"
            className="mt-4 flex items-center gap-2 text-uf-text-muted hover:text-uf-text text-xs"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Scan another
          </button>
        </div>
      )}

      {/* Scan Error */}
      {scanError && !processing && (
        <div data-testid="scan-error" className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <ShieldX className="w-5 h-5 text-red-400" />
            <p className="text-red-400 text-sm">{scanError}</p>
          </div>
          <button
            onClick={() => setScanError(null)}
            className="mt-2 text-uf-text-muted text-xs hover:text-uf-text"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Recent Scans */}
      {recentScans.length > 0 && (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
          <h4 className="text-sm font-semibold text-uf-text mb-3">Recent Scans</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recentScans.map((scan, i) => (
              <div
                key={i}
                data-testid={`recent-scan-${i}`}
                className="flex items-center gap-3 py-2 border-b border-uf-border/30 last:border-0"
              >
                <div className={`w-2 h-2 rounded-full ${scan.allowed ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-uf-text text-sm truncate">{scan.name}</p>
                  <p className="text-uf-text-muted text-xs">
                    {scan.direction === 'entry' ? 'Entry' : 'Exit'}
                    {scan.reason && ` — ${scan.reason}`}
                  </p>
                </div>
                <span className="text-uf-text-muted text-xs whitespace-nowrap">
                  {scan.time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ MEMBER LOOKUP COMPONENT ============
const MemberLookupTab = ({ token }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const searchMembers = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/members?search=${encodeURIComponent(search)}&page_size=20`, { headers });
      if (res.ok) {
        const d = await res.json();
        setResults(d.items || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div data-testid="staff-member-lookup">
      <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4">
        <h3 className="text-sm font-semibold text-uf-text mb-3">Member Lookup</h3>
        <div className="flex gap-2">
          <input
            value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchMembers()}
            placeholder="Search by name, email or member number..."
            className="flex-1 bg-uf-bg border border-uf-border rounded-lg px-4 py-2 text-uf-text text-sm placeholder:text-uf-text-muted"
            data-testid="member-search-input"
          />
          <button onClick={searchMembers} disabled={loading}
            className="bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="member-search-btn">
            {loading ? '...' : 'Search'}
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className={`${selected ? 'w-5/12' : 'w-full'} transition-all`}>
          {results.length > 0 && (
            <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
              <div className="divide-y divide-uf-border/50 max-h-[500px] overflow-y-auto">
                {results.map(m => (
                  <div key={m.id} onClick={() => setSelected(m)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-uf-surface-hi transition-colors ${selected?.id === m.id ? 'bg-uf-surface-hi' : ''}`}
                    data-testid={`member-result-${m.id}`}>
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-emerald-400">{m.first_name?.[0]}{m.last_name?.[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-uf-text font-medium text-sm truncate">{m.first_name} {m.last_name}</p>
                      <p className="text-uf-text-muted text-xs truncate">{m.email} · {m.member_number}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${m.subscription_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {m.subscription_status || 'unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {results.length === 0 && search && !loading && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted text-sm">No members found</div>
          )}
        </div>
        {selected && (
          <div className="w-7/12 bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="member-detail-card">
            <div className="p-5 border-b border-uf-border">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 ring-2 ring-emerald-500/30 flex items-center justify-center">
                    <span className="text-xl font-bold text-emerald-400">{selected.first_name?.[0]}{selected.last_name?.[0]}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-uf-text">{selected.first_name} {selected.last_name}</h3>
                    <p className="text-uf-text-muted text-xs">{selected.member_number} · {selected.email}</p>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} className="text-uf-text-muted hover:text-uf-text p-1"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-uf-text-muted text-xs">Phone</p><p className="text-uf-text">{selected.phone || '-'}</p></div>
                <div><p className="text-uf-text-muted text-xs">Status</p><p className="text-uf-text capitalize">{selected.subscription_status || 'N/A'}</p></div>
                <div><p className="text-uf-text-muted text-xs">Home Site</p><p className="text-uf-text">{selected.home_site_name || 'All'}</p></div>
                <div><p className="text-uf-text-muted text-xs">Joined</p><p className="text-uf-text">{selected.created_at?.split('T')[0] || '-'}</p></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StaffPortal = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const handleSignOut = () => { logout(); navigate('/login'); };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [staffData, setStaffData] = useState(null);
  const [gymStats, setGymStats] = useState(null);
  const [myRota, setMyRota] = useState([]);
  const [myHolidays, setMyHolidays] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [savingAvail, setSavingAvail] = useState(false);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return d.toISOString().split('T')[0];
  });
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ start_date: '', end_date: '', reason: '' });
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchStaffData(); fetchGymStats(); }, []);
  useEffect(() => { if (activeTab === 'rota') fetchMyRota(); }, [activeTab, weekStart]);
  useEffect(() => { if (activeTab === 'holidays') fetchMyHolidays(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'availability') fetchAvailability(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'colleagues') fetchColleagues(); }, [activeTab]);

  const fetchStaffData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers });
      if (res.ok) { const d = await res.json(); setStaffData(d); setEditData({ first_name: d.first_name || '', last_name: d.last_name || '', phone: d.phone || '' }); }
    } catch {}
  };

  const fetchGymStats = async () => {
    try { const res = await fetch(`${API_URL}/api/reporting/gym-stats`, { headers }); if (res.ok) setGymStats(await res.json()); } catch {}
  };

  const fetchMyRota = async () => {
    try {
      const siteId = user?.site_ids?.[0] || '';
      if (!siteId) { setMyRota([]); return; }
      const res = await fetch(`${API_URL}/api/staff-management/rota?site_id=${siteId}&week_start=${weekStart}`, { headers });
      if (!res.ok) { setMyRota([]); return; }
      const data = await res.json();
      const items = Array.isArray(data) ? data : data?.items || [];
      setMyRota(items.filter(r => r.staff_id === user?.id));
    } catch { setMyRota([]); }
  };

  const fetchMyHolidays = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff-management/holidays`, { headers });
      const data = await res.json();
      setMyHolidays((data.items || []).filter(h => h.staff_id === user?.id));
    } catch {}
  };

  const fetchColleagues = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff?page_size=100`, { headers });
      if (res.ok) { const d = await res.json(); setColleagues((d.items || []).filter(s => s.id !== user?.id && s.is_active)); }
    } catch {}
  };

  const fetchAvailability = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff-management/availability/${user?.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data && data.length) { setAvailability(data); }
        else { setAvailability(DAYS.map(day => ({ day_of_week: day.toLowerCase(), morning: true, afternoon: true, evening: true }))); }
      }
    } catch { setAvailability(DAYS.map(day => ({ day_of_week: day.toLowerCase(), morning: true, afternoon: true, evening: true }))); }
  };

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await fetch(`${API_URL}/api/staff-management/availability`, { method: 'PUT', headers, body: JSON.stringify(availability) });
    } catch {} finally { setSavingAvail(false); }
  };

  const toggleSlot = (dayIdx, slot) => {
    setAvailability(prev => prev.map((a, i) => i === dayIdx ? { ...a, [slot]: !a[slot] } : a));
  };

  const submitHoliday = async () => {
    if (!newHoliday.start_date || !newHoliday.end_date) return;
    await fetch(`${API_URL}/api/staff-management/holidays`, {
      method: 'POST', headers, body: JSON.stringify({ ...newHoliday, site_id: user?.site_ids?.[0] || '' })
    });
    setShowHolidayForm(false);
    setNewHoliday({ start_date: '', end_date: '', reason: '' });
    fetchMyHolidays();
  };

  const saveProfile = async () => {
    await fetch(`${API_URL}/api/staff/${user?.id}`, { method: 'PATCH', headers, body: JSON.stringify(editData) });
    setEditMode(false);
    fetchStaffData();
  };

  const shiftWeek = (dir) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + (dir * 7));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  const ADMIN_ROLES = ['super_admin', 'developer_admin', 'area_manager', 'site_manager', 'finance_admin', 'admin'];
  const isAdminRole = ADMIN_ROLES.includes(user?.role);

  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'scanner', label: 'Scanner', icon: ScanLine, adminOnly: true },
    { id: 'members', label: 'Member Lookup', icon: Users, adminOnly: true },
    { id: 'rota', label: 'My Rota', icon: Calendar },
    { id: 'availability', label: 'Availability', icon: ClipboardList },
    { id: 'holidays', label: 'Holidays', icon: Umbrella },
    { id: 'colleagues', label: 'Colleagues', icon: UserCheck },
    { id: 'profile', label: 'My Details', icon: User },
    { id: 'qr', label: 'My Pass', icon: QrCode },
  ];
  const tabs = allTabs.filter(t => isAdminRole || !t.adminOnly);

  const statusColor = (s) => s === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : s === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400';

  return (
    <div className="min-h-screen bg-uf-bg">
      <div className="bg-uf-surface border-b border-uf-border px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-xl font-bold text-uf-text" data-testid="staff-portal-greeting">Welcome back, {user?.first_name}</h1>
            <p className="text-uf-text-muted text-xs mt-0.5">{user?.employee_number} &middot; {user?.role?.replace('_',' ')}</p>
          </div>
          <div className="flex items-center gap-4">
            {isAdminRole && <Link to="/admin" className="text-uf-text-muted hover:text-uf-red text-sm flex items-center gap-1.5 transition-colors" data-testid="back-to-admin-link">
              <ChevronLeft className="w-4 h-4" /> Back to Admin
            </Link>}
            <div className="flex items-center gap-2 text-uf-text-muted text-xs">
              <Building2 className="w-3.5 h-3.5" /><span>UltraFlex</span>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-uf-surface-hi hover:bg-uf-red/20 hover:text-uf-red text-uf-text-muted text-xs font-medium transition-colors"
              data-testid="staff-portal-signout-btn"
              title="Sign out of UltraFlex"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-8">
        <div className="flex gap-1 mb-6 bg-uf-surface rounded-lg p-1 overflow-x-auto" data-testid="staff-portal-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`portal-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-uf-red text-white' : 'text-uf-text-muted hover:text-uf-text'}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* ===== DASHBOARD ===== */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6" data-testid="staff-dashboard">
            {/* Announcements */}
            <div className="bg-gradient-to-r from-uf-red/10 to-uf-surface rounded-xl border border-uf-red/20 p-5">
              <div className="flex items-center gap-3 mb-2">
                <Megaphone className="w-5 h-5 text-uf-red" />
                <h3 className="text-sm font-semibold text-uf-text">Announcements</h3>
              </div>
              <div className="space-y-2">
                <div className="bg-uf-surface/50 rounded-lg p-3">
                  <p className="text-uf-text text-sm font-medium">February Rota Published</p>
                  <p className="text-uf-text-muted text-xs">All February shifts have been finalised. Please check your schedule and flag any issues with your site manager.</p>
                </div>
                <div className="bg-uf-surface/50 rounded-lg p-3">
                  <p className="text-uf-text text-sm font-medium">New Member Sign-Up Drive</p>
                  <p className="text-uf-text-muted text-xs">We're running a referral campaign this month. Encourage members to bring a friend for a free day pass!</p>
                </div>
              </div>
            </div>

            {gymStats ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Users} label="Active Members" value={gymStats.total_active_members} color="text-emerald-400" />
                  <StatCard icon={TrendingUp} label="New This Month" value={gymStats.new_members_this_month}
                    sub={`${gymStats.growth_percentage > 0 ? '+' : ''}${gymStats.growth_percentage}% vs last month`}
                    color={gymStats.growth_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'} />
                  <StatCard icon={Target} label="New Leads" value={gymStats.new_leads_this_month} color="text-blue-400" />
                  <StatCard icon={Calendar} label="Day Passes (Month)" value={gymStats.day_passes_this_month} color="text-violet-400" />
                </div>
                {gymStats.busiest_site && (
                  <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
                    <div className="flex items-center gap-3">
                      <Award className="w-8 h-8 text-amber-400" />
                      <div>
                        <p className="text-uf-text-muted text-xs uppercase tracking-wider">Busiest Site</p>
                        <p className="text-uf-text text-lg font-bold">{gymStats.busiest_site.site_name}</p>
                        <p className="text-uf-text-muted text-sm">{gymStats.busiest_site.active_members} active members</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
                  <h3 className="text-sm font-semibold text-uf-text mb-3">Top Sites by Membership</h3>
                  <div className="space-y-2">
                    {(gymStats.site_breakdown || []).map((s, i) => (
                      <div key={s.site_id} className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-uf-surface-hi text-uf-text-muted'}`}>{i + 1}</span>
                        <span className="text-uf-text text-sm flex-1">{s.site_name}</span>
                        <span className="text-uf-text font-semibold">{s.active_members}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
                  <h3 className="text-sm font-semibold text-uf-text mb-3">Membership Growth (Last 6 Months)</h3>
                  <div className="flex items-end gap-3 h-32">
                    {(gymStats.monthly_growth || []).map((m, i) => {
                      const max = Math.max(...(gymStats.monthly_growth || []).map(g => g.new_members), 1);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end">
                          <span className="text-uf-text text-xs font-bold mb-1">{m.new_members}</span>
                          <div className="w-full bg-emerald-500/60 rounded-t transition-all" style={{ height: `${Math.max((m.new_members / max) * 80, m.new_members ? 4 : 0)}px` }} />
                          <span className="text-[10px] text-uf-text-muted mt-1">{m.month?.split(' ')[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
              </div>
            )}
          </div>
        )}

        {/* ===== BACKUP SCANNER ===== */}
        {activeTab === 'scanner' && (
          <StaffBackupScanner token={token} user={user} />
        )}

        {/* ===== MEMBER LOOKUP ===== */}
        {activeTab === 'members' && (
          <MemberLookupTab token={token} />
        )}

        {/* ===== MY ROTA ===== */}
        {activeTab === 'rota' && (
          <div data-testid="staff-rota">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => shiftWeek(-1)} className="p-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text-muted hover:text-uf-text"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-uf-text text-sm font-medium">Week of {weekStart}</span>
              <button onClick={() => shiftWeek(1)} className="p-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text-muted hover:text-uf-text"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {weekDays.map((d, i) => {
                const dateStr = d.toISOString().split('T')[0];
                const entry = myRota.find(r => r.date === dateStr);
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                return (
                  <div key={i} className={`bg-uf-surface rounded-xl border p-4 text-center cursor-pointer hover:bg-uf-surface-hi transition-colors ${isToday ? 'border-uf-red' : 'border-uf-border'}`}
                    onClick={() => { 
                      const dayLabel = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
                      if (entry) {
                        alert(`${dayLabel}\n\nShift: ${entry.shift_name || 'Assigned'}\nTime: ${entry.start_time || 'N/A'} - ${entry.end_time || 'N/A'}${entry.notes ? '\nNotes: ' + entry.notes : ''}`);
                      } else {
                        alert(`${dayLabel}\n\nNo shift scheduled.\nYou are off on this day.`);
                      }
                    }}
                    data-testid={`rota-day-${dateStr}`}>
                    <p className={`text-xs font-medium ${isToday ? 'text-uf-red' : 'text-uf-text-muted'}`}>{d.toLocaleDateString('en-GB', { weekday: 'short' })}</p>
                    <p className={`text-lg font-bold ${isToday ? 'text-uf-red' : 'text-uf-text'}`}>{d.getDate()}</p>
                    {entry ? (
                      <div className="mt-2 bg-uf-red/10 rounded-lg p-2">
                        <p className="text-uf-red text-xs font-medium">{entry.shift_name || 'Shift'}</p>
                        <p className="text-uf-text-muted text-[10px]">{entry.start_time} - {entry.end_time}</p>
                      </div>
                    ) : (
                      <p className="text-uf-text-muted text-xs mt-2">Off</p>
                    )}
                  </div>
                );
              })}
            </div>
            {!user?.site_ids?.[0] && <p className="text-uf-text-muted text-sm text-center mt-4">No site assigned - contact your manager</p>}
          </div>
        )}

        {/* ===== AVAILABILITY ===== */}
        {activeTab === 'availability' && (
          <div data-testid="staff-availability">
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-uf-text">Weekly Availability</h3>
                  <p className="text-uf-text-muted text-xs mt-0.5">Toggle the slots you are available for. Green = available.</p>
                </div>
                <button onClick={saveAvailability} disabled={savingAvail} data-testid="save-availability-btn"
                  className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                  <Save className="w-4 h-4" />{savingAvail ? 'Saving...' : 'Save'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-uf-text-muted text-xs">
                      <th className="text-left py-2 pr-4 w-28">Day</th>
                      {SLOTS.map(s => <th key={s} className="text-center py-2 px-2 capitalize">{s}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {availability.map((a, idx) => (
                      <tr key={a.day_of_week} className="border-t border-uf-border/50">
                        <td className="py-3 pr-4 text-uf-text font-medium capitalize">{a.day_of_week}</td>
                        {SLOTS.map(slot => (
                          <td key={slot} className="py-3 px-2 text-center">
                            <button onClick={() => toggleSlot(idx, slot)}
                              data-testid={`avail-${a.day_of_week}-${slot}`}
                              className={`w-12 h-8 rounded-lg transition-colors font-medium text-xs ${a[slot] ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/40' : 'bg-uf-surface-hi text-uf-text-muted'}`}>
                              {a[slot] ? <Check className="w-4 h-4 mx-auto" /> : <X className="w-4 h-4 mx-auto" />}
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== HOLIDAYS ===== */}
        {activeTab === 'holidays' && (
          <div data-testid="staff-holidays">
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowHolidayForm(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="request-holiday-btn">
                <Umbrella className="w-4 h-4" />Request Holiday
              </button>
            </div>
            {showHolidayForm && (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-5 mb-4">
                <h3 className="text-sm font-semibold text-uf-text mb-3">New Holiday Request</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input type="date" value={newHoliday.start_date} onChange={e => setNewHoliday({...newHoliday, start_date: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="holiday-start" />
                  <input type="date" value={newHoliday.end_date} onChange={e => setNewHoliday({...newHoliday, end_date: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="holiday-end" />
                </div>
                <input placeholder="Reason (optional)" value={newHoliday.reason} onChange={e => setNewHoliday({...newHoliday, reason: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm mb-3" data-testid="holiday-reason" />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowHolidayForm(false)} className="px-4 py-2 text-uf-text-muted text-sm">Cancel</button>
                  <button data-testid="submit-holiday-btn" onClick={submitHoliday} className="px-4 py-2 bg-uf-red text-white rounded-lg text-sm">Submit Request</button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {myHolidays.map(h => (
                <div key={h.id} className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center"><Umbrella className="w-5 h-5 text-cyan-400" /></div>
                  <div className="flex-1">
                    <p className="text-uf-text font-medium text-sm">{h.start_date} to {h.end_date}</p>
                    {h.reason && <p className="text-uf-text-muted text-xs">{h.reason}</p>}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColor(h.status)}`}>{h.status}</span>
                </div>
              ))}
              {!myHolidays.length && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No holiday requests</div>}
            </div>
          </div>
        )}

        {/* ===== COLLEAGUES ===== */}
        {activeTab === 'colleagues' && (
          <div data-testid="staff-colleagues">
            <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
              <div className="px-5 py-3 border-b border-uf-border">
                <h3 className="text-sm font-semibold text-uf-text">Team Directory ({colleagues.length} colleagues)</h3>
              </div>
              {colleagues.length ? (
                <div className="divide-y divide-uf-border/50">
                  {colleagues.map(c => (
                    <div key={c.id} className="flex items-center gap-4 px-5 py-3 hover:bg-uf-surface-hi transition-colors">
                      <div className="w-9 h-9 rounded-full bg-uf-red/10 ring-1 ring-uf-red/20 flex items-center justify-center">
                        <span className="text-uf-red text-xs font-bold">{c.first_name?.[0]}{c.last_name?.[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-uf-text text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
                        <p className="text-uf-text-muted text-xs capitalize">{(c.role || '').replace('_', ' ')}</p>
                      </div>
                      {c.employee_number && <span className="text-uf-text-muted text-xs font-mono hidden sm:block">{c.employee_number}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-uf-text-muted">No colleagues found</div>
              )}
            </div>
          </div>
        )}

        {/* ===== MY DETAILS ===== */}
        {activeTab === 'profile' && (
          <div className="max-w-lg" data-testid="staff-profile">
            <div className="bg-uf-surface rounded-xl border border-uf-border p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-uf-red/20 ring-2 ring-uf-red/30 flex items-center justify-center">
                  <span className="text-2xl font-bold text-uf-red">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-uf-text">{staffData?.first_name || user?.first_name} {staffData?.last_name || user?.last_name}</h3>
                  <p className="text-uf-text-muted text-xs">{user?.employee_number} &middot; {user?.email}</p>
                </div>
                <button onClick={() => setEditMode(!editMode)} className="ml-auto p-2 text-uf-text-muted hover:text-uf-text"><Edit3 className="w-4 h-4" /></button>
              </div>
              {editMode ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-uf-text-muted text-xs block mb-1">First Name</label>
                      <input value={editData.first_name} onChange={e => setEditData({...editData, first_name: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" /></div>
                    <div><label className="text-uf-text-muted text-xs block mb-1">Last Name</label>
                      <input value={editData.last_name} onChange={e => setEditData({...editData, last_name: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" /></div>
                  </div>
                  <div><label className="text-uf-text-muted text-xs block mb-1">Phone</label>
                    <input value={editData.phone} onChange={e => setEditData({...editData, phone: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" /></div>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => setEditMode(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
                    <button data-testid="save-profile-btn" onClick={saveProfile} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm flex items-center justify-center gap-2"><Save className="w-4 h-4" />Save</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <InfoRow icon={Mail} label="Email" value={user?.email} />
                  <InfoRow icon={Phone} label="Phone" value={staffData?.phone || 'Not set'} />
                  <InfoRow icon={Hash} label="Employee Number" value={user?.employee_number} />
                  <InfoRow icon={Building2} label="Role" value={user?.role?.replace('_', ' ')} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== QR CODE ===== */}
        {activeTab === 'qr' && (
          <div className="flex flex-col items-center gap-6" data-testid="staff-qr">
            <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center max-w-sm w-full">
              <p className="text-uf-text-muted text-xs uppercase tracking-wider mb-4">Staff Access Pass</p>
              <div className="bg-white p-6 rounded-xl inline-block">
                <QRCode value={JSON.stringify({ type: 'staff', employee_number: user?.employee_number, id: user?.id })} size={220} />
              </div>
              <p className="text-uf-text text-2xl font-bold mt-4">{user?.employee_number}</p>
              <p className="text-uf-text-muted text-sm">{user?.first_name} {user?.last_name}</p>
              <p className="text-uf-text-muted text-xs mt-1 capitalize">{user?.role?.replace('_', ' ')}</p>
              <p className="text-uf-text-muted text-xs mt-4">Present this QR code at any site entrance</p>
            </div>
            {/* Digital Wallet Pass */}
            {user?.id && (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-6 max-w-sm w-full" data-testid="staff-wallet-section">
                <WalletButtons passType="staff" entityId={user.id} showPreview={true} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
    <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-uf-text-muted" /><p className="text-uf-text-muted text-xs uppercase tracking-wider">{label}</p></div>
    <p className={`text-3xl font-bold ${color}`}>{value ?? 0}</p>
    {sub && <p className={`text-xs mt-1 ${color}`}>{sub}</p>}
  </div>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2 border-b border-uf-border/50 last:border-0">
    <Icon className="w-4 h-4 text-uf-text-muted" />
    <div><p className="text-uf-text-muted text-[10px] uppercase tracking-wider">{label}</p><p className="text-uf-text text-sm capitalize">{value || '-'}</p></div>
  </div>
);

export default StaffPortal;
